import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { CmmdProject, EnvironmentId } from "@cmmd-forge/contracts";
import { localDeviceLabel } from "./lib/utils";

export type ProjectImportStatus = "cloning" | "ready" | "failed";
/**
 * Kinds of import that can be in flight. `git-url` is gone with the unreachable
 * Git URL source; a job persisted under it still renders correctly, because the
 * only branch on this value asks whether it is `blank`.
 */
export type ProjectImportSource = "blank" | "github";
/**
 * Which sort of place an import lands in. This is what a running job, a job id
 * and a destination label need, and all three are indifferent to which machine
 * was picked.
 */
export type ProjectImportDestinationKind = "forge-cloud" | "desktop-local";

/**
 * Where an import lands: the sort of place, and the Environment itself.
 *
 * A person can own more than one machine and, since CMMD keyed Environments to
 * (organization, user), the cloud side is a named Environment rather than a
 * request to make one. A bare "cloud or local" answer cannot say which of two
 * machines was picked, so the "where" step's answer is carried whole.
 *
 * `environmentId` is null when the choice is "provision one", which is the only
 * case where no Environment exists to name.
 */
export interface ProjectImportDestination {
  readonly kind: ProjectImportDestinationKind;
  readonly environmentId: EnvironmentId | null;
}

export function toProjectImportDestination(
  kind: ProjectImportDestinationKind,
  environmentId: EnvironmentId | null = null,
): ProjectImportDestination {
  return { kind, environmentId };
}

/**
 * Label for an import destination. The local one is derived from the running
 * platform (Forge ships on macOS, Windows and Linux) rather than hardcoded to a
 * Mac. Resolved per call rather than as a frozen record: a module-level constant
 * is evaluated at import time, when a pre-hydration bundle has no `navigator`
 * and would bake in the "This device" fallback for the life of the page.
 */
export function projectImportDestinationLabel(
  destination: ProjectImportDestinationKind,
  platform?: string,
): string {
  return destination === "forge-cloud" ? "Forge Cloud" : localDeviceLabel(platform);
}

/**
 * "Skip planning context" is only valid for desktop-local imports. A Forge Cloud
 * runtime is keyed on the CMMD project id (forge-o{org}-u{user}-p{projectId}) and
 * the CMMD /ensure route rejects a missing projectId with 400 — so skipping the
 * CMMD project for a cloud import always fails. The picker must not offer it.
 */
export function canSkipCmmdPlanningContext(destination: ProjectImportDestinationKind): boolean {
  return destination !== "forge-cloud";
}

export interface ProjectImportJob {
  readonly id: string;
  readonly source: ProjectImportSource;
  readonly destination: ProjectImportDestinationKind;
  readonly label: string;
  readonly detail: string;
  readonly phase: string | null;
  readonly percent: number | null;
  readonly current: number | null;
  readonly total: number | null;
  readonly status: ProjectImportStatus;
  readonly startedAt: string;
  readonly updatedAt: string;
  readonly error: string | null;
  /**
   * True while the job is blocked on the user completing an out-of-band step
   * (e.g. approving the CMMD sign-in that opened in their browser). The row
   * renders a distinct "Action needed" treatment so the instruction is not
   * mistaken for passive progress. Set by the connect flow's slow-connect
   * signal; cleared automatically by the next progress update.
   */
  readonly actionRequired: boolean;
  readonly pendingCmmdContext: ProjectImportPendingCmmdContext | null;
}

export interface ProjectImportPendingCmmdContext {
  readonly repoFullName: string;
  readonly project: CmmdProject;
  readonly organizationId: number;
  readonly campaignName: string | null;
  readonly source: "created_from_cmmd";
}

export interface ProjectImportState {
  readonly jobs: readonly ProjectImportJob[];
  readonly upsertJob: (
    input: Omit<
      ProjectImportJob,
      | "phase"
      | "percent"
      | "current"
      | "total"
      | "startedAt"
      | "updatedAt"
      | "error"
      | "status"
      | "actionRequired"
      | "pendingCmmdContext"
    > & {
      readonly phase?: string | null;
      readonly percent?: number | null;
      readonly current?: number | null;
      readonly total?: number | null;
      readonly status?: ProjectImportStatus;
      readonly error?: string | null;
      readonly pendingCmmdContext?: ProjectImportPendingCmmdContext | null;
    },
  ) => void;
  readonly markReady: (id: string) => void;
  readonly markFailed: (id: string, error: string) => void;
  readonly updateProgress: (
    id: string,
    progress: Partial<
      Pick<
        ProjectImportJob,
        "detail" | "phase" | "percent" | "current" | "total" | "actionRequired"
      >
    >,
  ) => void;
  readonly dismissJob: (id: string) => void;
}

export const PROJECT_IMPORT_STORAGE_KEY = "forge:project-import-jobs:v1";
const RETIRED_DESKTOP_CLOUD_CONNECT_CHANNEL = "desktop-auth:connect-cloud-runtime";

function createMemoryStorage(): Storage {
  const store = new Map<string, string>();
  return {
    clear: () => store.clear(),
    getItem: (key) => store.get(key) ?? null,
    key: (index) => Array.from(store.keys())[index] ?? null,
    get length() {
      return store.size;
    },
    removeItem: (key) => {
      store.delete(key);
    },
    setItem: (key, value) => {
      store.set(key, value);
    },
  };
}

const fallbackProjectImportStorage = createMemoryStorage();

function resolveProjectImportStorage(): Storage {
  if (typeof window !== "undefined") return window.localStorage;
  const globalStorage = (globalThis as { readonly localStorage?: Storage }).localStorage;
  return globalStorage ?? fallbackProjectImportStorage;
}

export function githubProjectImportJobId(
  repoFullName: string,
  destination: ProjectImportDestinationKind = "forge-cloud",
): string {
  return `github:${destination}:${repoFullName.toLowerCase()}`;
}

export function blankProjectImportJobId(
  name: string,
  destination: ProjectImportDestinationKind = "forge-cloud",
): string {
  return `blank:${destination}:${name.trim().toLowerCase()}`;
}

export const useProjectImportStore = create<ProjectImportState>()(
  persist(
    (set) => ({
      jobs: [],
      upsertJob: (input) =>
        set((state) => {
          const now = new Date().toISOString();
          const existing = state.jobs.find((job) => job.id === input.id);
          const nextJob: ProjectImportJob = {
            id: input.id,
            source: input.source,
            destination: input.destination,
            label: input.label,
            detail: input.detail,
            phase: input.phase ?? existing?.phase ?? null,
            percent: input.percent ?? existing?.percent ?? null,
            current: input.current ?? existing?.current ?? null,
            total: input.total ?? existing?.total ?? null,
            status: input.status ?? "cloning",
            startedAt: existing?.startedAt ?? now,
            updatedAt: now,
            error: input.error ?? null,
            actionRequired: false,
            pendingCmmdContext:
              input.pendingCmmdContext !== undefined
                ? input.pendingCmmdContext
                : (existing?.pendingCmmdContext ?? null),
          };
          return {
            jobs: existing
              ? state.jobs.map((job) => (job.id === input.id ? nextJob : job))
              : [nextJob, ...state.jobs],
          };
        }),
      markReady: (id) =>
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === id
              ? { ...job, status: "ready", updatedAt: new Date().toISOString(), error: null }
              : job,
          ),
        })),
      markFailed: (id, error) =>
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === id
              ? { ...job, status: "failed", updatedAt: new Date().toISOString(), error }
              : job,
          ),
        })),
      updateProgress: (id, progress) =>
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === id && job.status === "cloning"
              ? // A new progress tick means the flow advanced, so clear any prior
                // "action needed" state unless this update re-asserts it.
                { ...job, actionRequired: false, ...progress, updatedAt: new Date().toISOString() }
              : job,
          ),
        })),
      dismissJob: (id) =>
        set((state) => ({
          jobs: state.jobs.filter((job) => job.id !== id),
        })),
    }),
    {
      name: PROJECT_IMPORT_STORAGE_KEY,
      storage: createJSONStorage(resolveProjectImportStorage),
      partialize: (state) => ({ jobs: state.jobs }),
      version: 3,
      migrate: (persisted, version) => {
        const state = persisted as { jobs?: readonly Record<string, unknown>[] } | undefined;
        if (!state?.jobs) return persisted as ProjectImportState;
        return {
          ...state,
          jobs: state.jobs
            .filter((job) => !isRetiredDesktopCloudConnectFailure(job))
            .map((job) =>
              version < 2
                ? // v1 jobs predate `actionRequired`; default it so the field
                  // is never undefined at runtime.
                  Object.assign({ actionRequired: false }, job)
                : job,
            ),
        } as unknown as ProjectImportState;
      },
    },
  ),
);

function isRetiredDesktopCloudConnectFailure(job: Record<string, unknown>): boolean {
  if (job.status !== "failed" || job.destination !== "forge-cloud") return false;
  const error = typeof job.error === "string" ? job.error : "";
  const detail = typeof job.detail === "string" ? job.detail : "";
  return (
    error.includes(RETIRED_DESKTOP_CLOUD_CONNECT_CHANNEL) ||
    detail.includes(RETIRED_DESKTOP_CLOUD_CONNECT_CHANNEL)
  );
}
